import { useSubscription } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { useCallback, useMemo } from 'react';

import { useIsFeatureFlagEnabled } from '../../feature-flags/useIsFeatureFlagEnabled';
import { useTRPC } from '../trpc';
import type {
  SubscriptionHookConfig,
  SubscriptionHookFactoryResult,
} from './types';

/**
 * Portal-local mirror of the web `createSubscriptionHook` (RSP-6570). Builds a
 * GraphQL-subscription-to-tRPC wrapper hook that switches on the `trpc` feature
 * flag (org-wide, read via the portal's `useIsFeatureFlagEnabled`). The tRPC
 * branch emulates the live subscription with a data-driven poll
 * (`trpcRefetchInterval`) until tRPC gains an SSE transport (RSP-4320); the
 * GraphQL branch keeps the legacy Apollo subscription.
 *
 * Counterpart: `packages/web/src/utils/hook-factories/createSubscriptionHook.tsx`.
 * Same deal as `createMutationHook` — deliberately duplicated, and both copies
 * retire together with the `trpc` flag and the portal's Apollo removal.
 */
export const createSubscriptionHook = <
  TSubscriptionArgs,
  TTRPCOutput,
  TGraphQLData extends { __typename?: 'subscription_root' },
>(
  config: SubscriptionHookConfig<TSubscriptionArgs, TTRPCOutput, TGraphQLData>
): SubscriptionHookFactoryResult<TSubscriptionArgs, TGraphQLData> => {
  const {
    trpcQueryOptions,
    mapTrpcDataToGraphQL,
    graphqlDocument,
    graphqlVariables,
    graphqlFetchPolicy = 'no-cache',
    trpcStaleTime,
    trpcRefetchInterval,
    mapGraphQLData,
  } = config;

  return ({ queryArgs, shouldSkip }) => {
    const skip = shouldSkip ?? false;
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();

    // tRPC branch — a poll stands in for the live subscription (RSP-4320).
    const {
      data: trpcData,
      isLoading: trpcLoading,
      refetch: trpcRefetch,
      error: trpcError,
    } = useQuery<
      TTRPCOutput,
      TRPCClientErrorLike<{ transformer: true; errorShape: unknown }>
    >({
      ...trpcQueryOptions(trpc, queryArgs),
      enabled: trpcEnabled && !skip,
      staleTime: trpcStaleTime,
      refetchInterval: trpcRefetchInterval
        ? (query) => trpcRefetchInterval(query.state.data)
        : undefined,
    });

    const mappedTrpcData = useMemo(
      () => (trpcData ? mapTrpcDataToGraphQL(trpcData) : undefined),
      [trpcData]
    );

    const trpcRefetchMapped = useCallback(async () => {
      const { data: refetchData, error } = await trpcRefetch();

      return {
        data: refetchData ? mapTrpcDataToGraphQL(refetchData) : undefined,
        error: error ?? null,
      };
    }, [trpcRefetch]);

    // GraphQL branch — the legacy Apollo subscription.
    const graphqlVars = graphqlVariables && graphqlVariables(queryArgs);

    const {
      data: graphqlData,
      loading: graphqlLoading,
      error: graphqlError,
    } = useSubscription(graphqlDocument, {
      variables: graphqlVars,
      fetchPolicy: graphqlFetchPolicy,
      skip: trpcEnabled || skip,
    });

    const mappedGraphqlData = useMemo(
      () =>
        graphqlData && mapGraphQLData
          ? mapGraphQLData(graphqlData, queryArgs)
          : graphqlData,
      [graphqlData, queryArgs]
    );

    // Subscriptions auto-update, so refetch is a no-op on the GraphQL branch.
    const graphqlRefetchNoop = useCallback(
      () => Promise.resolve({ data: undefined, error: null }),
      []
    );

    if (trpcEnabled) {
      return {
        // A disabled query never reports loading; report loading only while
        // enabled so the flag-off path never wedges on a spinner.
        loading: skip ? false : trpcLoading,
        data: mappedTrpcData,
        refetch: trpcRefetchMapped,
        error: trpcError,
      };
    }

    return {
      loading: graphqlLoading,
      data: mappedGraphqlData,
      refetch: graphqlRefetchNoop,
      error: graphqlError,
    };
  };
};
